import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CANCEL_STATUS, FINISHED_STATUSES, LIVE_STATUSES, NOT_STARTED_STATUS, MATCH_STATUS as STATUS } from "@/contants";
import { formatDateTime } from "./format";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const getMatchDisplayTime = (liveScore, status, matchTime, halfStartTime, showLabelShort = false) => {
  const isCheckingVar = !!liveScore?.var;
  if (isCheckingVar) {
    return { type: 'var', content: 'VAR' };
  }

  const currentStatus = liveScore?.status !== undefined ? Number(liveScore.status) : status;
  let currentMinute = null;
  let showMatchTime = false;
  let matchStatus = "--";

  if (liveScore?.minute !== undefined || liveScore?.currentMinute !== undefined) {
    currentMinute = liveScore?.minute ?? liveScore?.currentMinute;
    showMatchTime = true;
  } else {
    const timeStart = liveScore?.halfStartTime || (currentStatus === STATUS.FIRST_HALF ? matchTime : halfStartTime);
    const currentTime = Math.floor(Date.now() / 1000);
    if (timeStart != null && timeStart !== "") {
      const startTimeUnix = !isNaN(Number(timeStart))
        ? Number(timeStart)
        : Math.floor(new Date(timeStart).getTime() / 1000);

      if (!isNaN(startTimeUnix)) {
        switch (currentStatus) {
          case STATUS.FIRST_HALF:
            showMatchTime = true;
            currentMinute = Math.floor((currentTime - startTimeUnix) / 60);
            if (currentMinute >= 45) currentMinute = `45+${currentMinute - 45}`;
            matchStatus = showLabelShort ? "H1" : "Hiệp 1";
            break;
          case STATUS.SECOND_HALF:
            showMatchTime = true;
            if (!Number.isFinite(Number(timeStart)) || Number(timeStart) <= 0) {
              currentMinute = 45;
              break;
            }
            currentMinute = 45 + Math.floor((currentTime - Number(timeStart)) / 60);
            if (!Number.isFinite(currentMinute) || currentMinute < 45) {
              currentMinute = 45;
              break;
            }
            if (currentMinute >= 90) currentMinute = `90+${currentMinute - 90}`;
            matchStatus = showLabelShort ? "H2" : "Hiệp 2";
            break;
          case STATUS.EXTRA_TIME:
            showMatchTime = true;
            currentMinute = `ET+${Math.floor((currentTime - startTimeUnix) / 60)}`;
            matchStatus = showLabelShort ? "HP" : "Hiệp phụ";
            break;
          case STATUS.HALF_TIME_BREAK:
            matchStatus = showLabelShort ? "HT" : "HẾT H1";
            break;
          case STATUS.NOT_STARTED:
            showMatchTime = false;
            const { time } = formatDateTime(matchTime);
            matchStatus = time;
            break;
          case STATUS.FINISHED:
            matchStatus = showLabelShort ? "FT" : "Kết Thúc";
            break;
          case STATUS.TERMINATED:
            matchStatus = showLabelShort ? "CAN" : "Huỷ";
            break;
          case STATUS.POSTPONED:
            matchStatus = showLabelShort ? "PST" : "Hoãn";
            break;
          case STATUS.INTERRUPTED:
            matchStatus = showLabelShort ? "INT" : "Gián Đoạn";
            break;
        }
      }
    } else if (currentStatus === STATUS.NOT_STARTED) {
      const { time } = formatDateTime(matchTime);
      matchStatus = time;
    }
  }

  if (showMatchTime && currentMinute !== null) {
    return { 
      type: 'minute', 
      content: currentMinute,
      halfLabel: matchStatus
    };
  }
  return { type: 'status', content: matchStatus };
};

export function formatCorrectScoresForGrid(correctScoresOdds) {
  if (!Array.isArray(correctScoresOdds) || !correctScoresOdds.length) return [];
  return correctScoresOdds.map((o) => ({
    label: `${o.homeScore}-${o.awayScore}`,
    value: o?.odds != null ? String(o.odds) : (getOddValue(o?.instantOdds, o?.initialOdds) ?? "-"),
  }));
}

export function splitCorrectScoresListToSections(list) {
  if (!Array.isArray(list) || !list.length) return { home: [], draw: [], away: [] };
  return list.reduce(
    (acc, curr) => {
      const [h, a] = (curr.label || "").split("-").map(Number);
      if (h > a) acc.home.push(curr);
      else if (h < a) acc.away.push(curr);
      else acc.draw.push(curr);
      return acc;
    },
    { home: [], draw: [], away: [] }
  );
}

// Lấy giá trị odd từ liveOdd hoặc preMatchOdd
export const getOddValue = (liveOdd, preMatchOdd) => {
  if (liveOdd) {
    return liveOdd;
  } else if (preMatchOdd) {
    return preMatchOdd;
  } else {
    return "-";
  }
};

// Định dạng odds theo bookmarkerExtend (HONGKONG, MALAY, INDO, DECIMAL)
export const formatOddsByBookmarkerExtend = (data, bookmarkerExtend) => {
  if (!data || data === "-") return "-";
  
  const decimalOdds = Number(data);
  if (isNaN(decimalOdds)) return data;

  switch (bookmarkerExtend) {
    case "hongkong":
      return data;
    case "decimal":
      const result = (decimalOdds + 1).toFixed(2);
      return result === "NaN" ||
        result === "Infinity" ||
        result === "-Infinity"
        ? "-"
        : result;
    case "malay":
      if (decimalOdds < 2.0) {
        const result = (decimalOdds - 1).toFixed(2);
        return result === "NaN" ||
          result === "Infinity" ||
          result === "-Infinity"
          ? "-"
          : result;
      } else {
        const result = (-1 / (decimalOdds - 1)).toFixed(2);
        return result === "NaN" ||
          result === "Infinity" ||
          result === "-Infinity"
          ? "-"
          : result;
      }
    case "indo":
      if (decimalOdds >= 1) {
        const result = decimalOdds;
        return result === "NaN" ||
          result === "Infinity" ||
          result === "-Infinity"
          ? "-"
          : result;
      } else {
        const result = (-1 / decimalOdds).toFixed(2);
        return result === "NaN" ||
          result === "Infinity" ||
          result === "-Infinity"
          ? "-"
          : result;
      }
    default:
      return data;
  }
};

// Định dạng màu sắc cho odds changes
export const colorOddsChanges = (trend) => {
  switch (trend) {
    case "unchanged":
      return "text-[var(--theme-text)]";
    case "decreased":
      return "text-vibrant-red";
    case "increased":
      return "text-medium-green";
    default:
      return "text-[var(--theme-text)]";
  }
};

// Lấy odds theo companyId từ object data
export const getOddByCompanyId = (useFirstResult, data, companyIdOdds) => {
  if (!data) return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    if (useFirstResult) return data[0] ?? null;
    return data.find((item) => item?.companyId == companyIdOdds) ?? null;
  }

  if (typeof data !== "object") return null;

  if (useFirstResult) {
    if (Object.keys(data).length === 0) {
      return null;
    }
    return data[Object.keys(data)[0]] ?? null;
  }
  return data[companyIdOdds] ?? null;
};

// Filter unique scores by company
export const filterUniqueScoresByCompany = (data) => {
  if (!Array.isArray(data)) return [];
  
  return Object.values(
    data.reduce((acc, item) => {
      const key = `${item.companyId}-${item.homeScore}-${item.awayScore}`;
      if (!acc[key]) {
        acc[key] = item;
      }
      return acc;
    }, {})
  );
};

export const getMatchStatus = (status) => {
  const isFinishedMatch = FINISHED_STATUSES.includes(
    Number(status)
  );
  const isLiveMatch = LIVE_STATUSES.includes(Number(status));
  
  const isUpcomingMatch = NOT_STARTED_STATUS.includes(Number(status));

  const isCanceledMatch = CANCEL_STATUS.includes(Number(status));

  return { isFinishedMatch, isLiveMatch, isUpcomingMatch, isCanceledMatch };
};

export const getMatchHeaderType = (status) => {
  const { isFinishedMatch, isLiveMatch } = getMatchStatus(status);
  
  return isFinishedMatch ? "finished" : isLiveMatch ? "live" : "upcoming";
};

export const getLivePhaseLabel = (statusNum) => {
  const status = Number(statusNum);
  if (status === STATUS.FIRST_HALF) return "H1";
  if (status === STATUS.SECOND_HALF) return "H2";
  if (status === STATUS.EXTRA_TIME) return "ET";
  if (status === STATUS.PENALTY) return "PEN";
  return "LIVE";
};

export function resolveOddsTrend(currentTrend, fallbackTrend) {
  if (currentTrend && currentTrend !== "unchanged") return currentTrend;
  if (fallbackTrend && fallbackTrend !== "unchanged") return fallbackTrend;
  return "unchanged";
}